import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import { Button } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { CaretRight, MapPin } from 'phosphor-react';

import Reviews from '../../components/pages/home/Reviews';
import track from '../../lib/track';
import combineStyles from '../../styles/combineStyles';
import { buttonStyles } from '../../styles/base';
import styles from '../../styles/index';
import HomeSearch from '../../components/pages/home/HomeSearch';
import { isMobile } from '../../lib/getDeviceSize';
import { formatUtmParams } from '../../lib/helpers';
import { schedulingLink } from '../../lib/constants';
import {
  SunnyKitchen,
  NearByParks,
  ReadyToHost,
} from '../../components/utility/Icons';
import PageBase from '../../components/layout/PageBase';

export interface Props {
  classes: any;
  router: any;
  utmCampaign: string;
  utmMedium: string;
  utmSource: string;
  utmTerm: string;
}

export const SearchHomes: React.FC<Props> = props => {
  const { classes, router } = props;

  const goToSearch = () => {
    router.push('/search');
  };

  const talkToUs = () => {
    const utmParams = formatUtmParams(props);
    window.open(`${schedulingLink}${utmParams}`, '_blank');
  };

  const startHomebuyingQuiz = () => {
    router.push({
      pathname: '/signup/onboarding',
      query: {
        stage: 'intro',
        part: 'basics',
        question: 'cover',
      },
    });
  };

  return (
    <PageBase
      title="Torii: Modern home search that's all about you"
      overrideRemarks="Find the right home faster with curated listing recommendations powered by AI. Browse thousands of listings and work with an expert local real estate agent."
      disableOverflow
    >
      <div className="page-container home-container row center-xs">
        {/*Section Hero*/}
        <section className={`col-xs-12 search-page-banner ${classes.homeBg}`}>
          <div className="home-hero">
            <div className="hero-text-container">
              <h2 className="home-hero-heading-text">
                Home search that <br className="no-desktop no-mobile" /> really
                gets you
              </h2>
              <div className="hero-location">
                <p>
                  Get straight to what you want with curated listing
                  recommendations powered by AI.
                </p>
              </div>
              <div className="hero-search">
                <HomeSearch />
                <div className="hero-search-container">
                  <MapPin
                    size={22}
                    color="#FF7350"
                    weight="fill"
                    style={{ marginRight: 5 }}
                  />
                  <span className="hero-search-text">
                    Available across all of Massachusetts
                  </span>
                </div>
              </div>
            </div>
            <img
              className="home-hero-image"
              alt="hero-banner-one"
              src="/static/img/landing_pages/search-page-hero-desktop.png"
            />
          </div>
          <article className="press-container press-container-searchHomes">
            <img
              src="/static/img/home/business-insider-logo.svg"
              className="press-icon"
              alt="Business-Insider"
            />
            <img
              src="/static/img/home/boston-globe-logo.svg"
              className="press-icon"
              alt="Boston-Globe"
            />
            <img
              src="/static/img/home/inman-logo.svg"
              className="press-icon"
              alt="Inman"
            />
          </article>
          <div className="home-hero-preview" />
          <article className="press-container press-container-searchHomes-mobile">
            <img
              src="/static/img/home/business-insider-logo.svg"
              className="press-icon"
              alt="Business-Insider"
            />
            <img
              src="/static/img/home/boston-globe-logo.svg"
              className="press-icon"
              alt="Boston-Globe"
            />
            <img
              src="/static/img/home/inman-logo.svg"
              className="press-icon"
              alt="Inman"
            />
          </article>
        </section>
        <img
          src={
            isMobile()
              ? '/static/img/home/hero-bottom-ellipse-mobile.svg'
              : '/static/img/home/hero-bottom-ellipse.svg'
          }
          style={{ width: '100%', marginTop: -1 }}
        />

        {/*Section Listing*/}
        <section className="col-xs-12 section-listing">
          <div className="listing-text-container">
            <span className="listing-category">Listings</span>
            <h1 className="listing-heading">
              Millions of listings. <br className="no-mobile" /> Right on time.
            </h1>
            <p className="listing-text">
              We know the housing market is hot, so we refresh our listings
              multiple times a minute and update you as soon as new matches hit
              the market so you’ll never miss the right one.
            </p>
            <div className="listing-button-container">
              <Button
                variant="contained"
                style={{ width: '100%', height: 56 }}
                className={classes.buttonPurple}
                size="large"
                onClick={() => goToSearch()}
              >
                <span className="listing-button">Browse listings</span>
              </Button>
            </div>
          </div>
          <div className="listing-preview" />
        </section>

        {/*Section Personalize*/}
        <section className="col-xs-12 section-personalize">
          <div className="personalize-preview" />
          <div className="search-home-text-container">
            <span className="personalize-category">Personalize</span>
            <h1 className="personalize-heading">
              Find the right home <br className="no-tablet no-mobile" /> faster
            </h1>
            <p className="personalize-text">
              Get straight to what you want with the smartest home search
              around. Receive AI-curated home recommendations and customize your
              search more than anywhere else. Choose the order in which room
              photos appear, as well as the quality that you're looking for in
              each room.
            </p>
            <p className="personalize-text">
              Is cooking your thing? See the kitchen first. Love to host? Only
              see homes with great living rooms and outdoor space.
            </p>
          </div>
        </section>

        {/*Section Personalization*/}
        <section className="col-xs-12 section-personalization">
          <div className="personalization-text-container">
            <span className="personalization-category">Personalization</span>
            <h1 className="personalization-heading">Tell us about yourself</h1>
            <p className="personalization-text">
              We want to get to know who you are and what you’re really looking
              for. Take our home buying personalization quiz to kick start your
              home search and get connected with one of our expert local agents.
            </p>
            <div className="personalization-button-container">
              <Button
                variant="contained"
                className={classes.buttonHomeOrange}
                size="large"
                onClick={() => startHomebuyingQuiz()}
              >
                <span className="homebuying-quiz-button">
                  Start homebuying quiz
                </span>
              </Button>
              <div className="quiz-button-two">
                <Button
                  variant="contained"
                  className={classes.buttonWhiteOrange}
                  size="large"
                  onClick={() => talkToUs()}
                >
                  <span className="homebuying-talk-button">Talk to us</span>
                  <CaretRight size={24} color="#FF7350" weight="regular" />
                </Button>
              </div>
            </div>
          </div>
          <div className="personalization-preview">
            <div className="kitchen-blob">
              <SunnyKitchen />
            </div>
            <div className="park-blob">
              <NearByParks />
            </div>
            <div className="host-blob">
              <ReadyToHost />
            </div>
          </div>
        </section>

        {/*Section Dashboard*/}
        <section className="col-xs-12 search-dashboard">
          <div className="search-home-text-container">
            <span className="dashboard-category">Home Dashboard</span>
            <h1 className="dashboard-heading">
              Manage your entire <br /> process
            </h1>
            <p className="dashboard-text">
              We know searching for a home can feel overwhelming, so we built a
              seamless dashboard to help you manage everything and stay
              organized. Discover listings, save favorites, and schedule
              showings - all in one place.
            </p>
          </div>
          <div className="dashboard-preview" />
        </section>

        {/*Section Phone Preview*/}
        <section className="col-xs-12 search-design">
          <div className="search-design-preview" />
          <div className="search-design-info">
            <span className="info-category">Mobile App</span>
            <h1 className="info-heading">
              State of the art <br /> search in your pocket
            </h1>
            <div className="design-text-container">
              <p>
                Take your search on the go with the Torii mobile app. Browse
                through listings, see market trends, and talk with our team
                anytime you have a question about a property.
              </p>
            </div>
            <div className="app-buttons-container">
              <Button
                className="ios-button"
                onClick={() =>
                  track.buttonClick(
                    'App Store Link',
                    'Home',
                    'https://itunes.apple.com/us/app/torii-real-estate/id1362196303?ls=1&mt=8',
                  )
                }
              >
                <a
                  data-alt="iOS link"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://itunes.apple.com/us/app/torii-real-estate/id1362196303?ls=1&mt=8"
                  className="get-started-button"
                >
                  <img src="/static/img/home/apple-logo-white.svg" />
                  <div className="button-tag">
                    <span className="button-cta">Download on the</span>
                    <span className="button-title">App Store</span>
                  </div>
                </a>
              </Button>
              <Button
                className="android-button"
                onClick={() =>
                  track.buttonClick(
                    'Google Play Link',
                    'Home',
                    'https://play.google.com/store/apps/details?id=com.toriiapp.android',
                  )
                }
              >
                <a
                  data-alt="Android link"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://play.google.com/store/apps/details?id=com.toriiapp.android"
                  className="get-started-button"
                >
                  <img src="/static/img/home/google-play-logo-white.svg" />
                  <div className="button-tag">
                    <span className="button-cta">Get it on</span>
                    <span className="button-title">Google Play</span>
                  </div>
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/*Section Reviews*/}
        <section className="col-xs-12 testimonials">
          <div className="blue-blob" />
          <div className="yellow-blob" />
          <Reviews autoSlide={true} title="Take their word for it" />
        </section>

        {/*Section Buy*/}
        <section className="col-xs-12 section-buy">
          <div className="buy-text-container">
            <h1 className="buy-heading">
              Looking to buy a <br /> home?
            </h1>
            <p className="buy-text">
              Work with a local Torii real estate agent through the entire
              process, and we’ll set you up with a lender, closing services, and
              everything else you’ll need. On top of that, we’ll give you up to
              10% of our commission back at close - how bout dat?
            </p>
            <div className="buy-button-container">
              <Button
                variant="contained"
                style={{ width: '100%', height: 56 }}
                className={classes.buttonWhite}
                size="large"
                onClick={() => router.push('/buy')}
              >
                <span className="buy-button">Learn more</span>
              </Button>
            </div>
          </div>
          <div className="buy-preview" />
        </section>
      </div>
    </PageBase>
  );
};

const mapStateToProps = (state: any) => ({
  utmCampaign: state.auth.utmCampaign,
  utmMedium: state.auth.utmMedium,
  utmSource: state.auth.utmSource,
  utmTerm: state.auth.utmTerm,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
});

const combinedStyles = combineStyles(buttonStyles, styles);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(combinedStyles)(withRouter(SearchHomes)));
